// ユーザー詳細.
import React, { FC, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import { RouteComponentProps } from 'react-router-dom';

import store from '../store/configureStore';

import * as AppUserActions from '../actions/AppUser';
import * as MoffAPIActions from '../actions/MoffAPI';
import * as HeaderActions from '../actions/Header';
import * as InterestCheckActions from '../actions/InterestCheck';
import * as LifeCheckActions from '../actions/LifeCheck';
import * as BarthelIndexActions from '../actions/Adl';
// import * as FunctionalTrainingPlanActions from '../actions/MoffCheck';
import * as ScientificNursingCarePromotionActions from '../actions/ScientificNursingCarePromotion';
import * as csvCreateActions from '../actions/csvCreate';
import * as FooterActions from '../actions/Footer';
import { createLifeCheckAPIInstance } from '../api/LifeCheck';
import { createInterestCheckAPIInstance } from '../api/InterestCheckAPI';
import { createBarthelIndexAPIInstance } from '../api/BarthelIndexAPI';
import { createMoffAPIInstance } from '../api/MoffAPI';

import * as Header from '../constants/Header';
import * as UserListField from '../constants/UserListField';

import {
  objectQuickSort,
  duplicateRepeatSort,
  csvFileDownload,
  waitTimeMs,
  osJudgement,
  dispatchIdentifierToGTM,
  isEmpty,
} from '../utils/commonUtil';

import PageLoader from '../components/Atoms/PageLoader';
import LifeCSVOutput from '../components/Organisms/LifeCSVOutput';
import {
  CSV_FILE_DOWNLOAD_INTERVAL,
  CSV_OUTPUT_ITEMS,
  SERVICE_CODE,
  ADL_CODE,
  SERVICE_CODE_SUPPORT,
} from '../constants/csvCreate';
import { ResponseData } from '../constants/AppUser';

type AllState = ReturnType<typeof store.getState>;

const LifeCSVOutputContainer: FC<RouteComponentProps> = ({ location }) => {
  const dispatch = useDispatch();
  const moffAPIGlobalState = useSelector((state: AllState) => state.moffAPI);
  const csvCreateGlobalState = useSelector((state: AllState) => state.csvCreate);
  const appUserGlobalState = useSelector((state: AllState) => state.appUser);
  const scientificNursingCarePromotionGlobalState = useSelector(
    (state: AllState) => state.scientificNursingCarePromotion,
  );
  // const interestCheckGlobalState = useSelector((state: AllState) => state.interestCheck);
  // const lifeCheckGlobalState = useSelector((state: AllState) => state.lifeCheck);
  // const functionalTrainingPlanGlobalState = useSelector((state: AllState) => state.functionalTrainingPlan);
  // const barthelIndexGlobalState = useSelector((state: AllState) => state.barthelIndex);

  const [date, setDate] = useState(moment());
  const [, setDateYYYYMM] = useState(moment().format('YYYY-MM'));
  const [cachedUsers, setCachedUsers] = useState<UserListField.User[]>([] as UserListField.User[]);
  const [cachedAppUsers, setCachedAppUsers] = useState<ResponseData[]>([] as ResponseData[]);
  const refUsersList = useRef<any>();

  const [toggleLifeCheck, setToggleLifeCheck] = useState(true);
  const [toggleInterestCheck, setToggleInterestCheck] = useState(false);
  const [toggleFunctionalTrainingPlan, setToggleFunctionalTrainingPlan] = useState(false);
  const [toggleAdl, setToggleAdl] = useState(false);
  const [toggleScientificNursing, setToggleScientificNursing] = useState(false);
  const [toggleUserInfo, setToggleUserInfo] = useState(false);
  const [outputItemNames, setOutputItemNames] = useState<CSV_OUTPUT_ITEMS['name'][]>([CSV_OUTPUT_ITEMS.lifeCheck.name]);
  const [serviceCode, setServiceCode] = useState<SERVICE_CODE>(SERVICE_CODE.dayCare);
  const [serviceCodeSupport, setServiceCodeSupport] = useState<SERVICE_CODE_SUPPORT>(SERVICE_CODE_SUPPORT.A6);
  const [adlCode, setAdlCode] = useState<ADL_CODE>(ADL_CODE.first);
  const [usersWithData, setUsersWithData] = useState<{ [key: string]: boolean }>({});
  const [isDataFetching, setIsDataFetching] = useState<boolean>(false);
  const [basicInfoUsers, setBasicInfoUsers] = useState<string[]>([]);

  // チェックボックスの状態が変更されたときに呼び出される関数
  const handleCheckChange = () => {
    // コンポーネント側で管理されているため、ここでは何もしない
  };

  // 最初に一回ユーザー一覧取得のために通信.
  useEffect(() => {
    document.title = 'LIFE CSV出力';
    dispatch(HeaderActions.setPage(Header.LIFE_CSV_OUTPUT));
    dispatch(FooterActions.display('hidden'));
    dispatch(MoffAPIActions.getUsers());
    dispatch(AppUserActions.checkAppUsers());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 利用者情報とuniqueId情報を取得したあとに、「生活機能チェック」のデータを取得
  useEffect(() => {
    if (
      _.isEmpty(cachedAppUsers) &&
      !_.isEmpty(moffAPIGlobalState.result) &&
      !_.isEmpty(appUserGlobalState.result?.uniqueIdsInfo)
    ) {
      console.log("'生活機能チェック'のデータを取得");
      const enabledUserIds = moffAPIGlobalState.result.map((user: UserListField.User) => user.user_id);
      const enabledUniqueIds = appUserGlobalState.result?.uniqueIdsInfo.filter((uniqueId: ResponseData) =>
        enabledUserIds.includes(uniqueId.app_user_id),
      );
      setCachedAppUsers(enabledUniqueIds);

      // 初期表示時は生活機能チェックのデータを取得
      dispatch(LifeCheckActions.getLifeCheckRecordByMonth(enabledUniqueIds, date.format('YYYY-MM')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moffAPIGlobalState.result, appUserGlobalState.Loaded]);

  // 日付が変更されたときに、選択されているタブに応じてデータを取得
  useEffect(() => {
    if (!_.isEmpty(cachedAppUsers)) {
      const currentMonth = date.format('YYYY-MM');

      if (toggleLifeCheck) {
        console.log("'生活機能チェック'のデータを取得");
        dispatch(LifeCheckActions.getLifeCheckRecordByMonth(cachedAppUsers, currentMonth));
      } else if (toggleInterestCheck) {
        console.log("'興味チェック'のデータを取得");
        dispatch(InterestCheckActions.getInterestCheckRecordByMonth(cachedAppUsers, currentMonth));
      } else if (toggleFunctionalTrainingPlan) {
        console.log("'個別機能訓練計画'のデータを取得");
        dispatch(MoffAPIActions.getFunctionalTrainingPlansFromTo(cachedAppUsers, currentMonth));
      } else if (toggleAdl) {
        console.log("'ADL'のデータを取得");
        dispatch(BarthelIndexActions.getBarthelIndexRecordByMonth(cachedAppUsers, currentMonth));
      } else if (toggleScientificNursing) {
        console.log("'科学的介護'のデータを取得");
        dispatch(
          ScientificNursingCarePromotionActions.getScientificNursingCarePromotionRecordByMonth(
            cachedAppUsers,
            currentMonth,
          ),
        );
      } else if (toggleUserInfo) {
        console.log("'ユーザー情報'のデータを取得");
        // 利用者基本情報を直接APIから取得
        (async () => {
          try {
            const [isError, moffAPI] = createMoffAPIInstance();
            if (!isError && moffAPI) {
              const response = await moffAPI.getMoffRakuUsersFunc();
              const basicInfoData = response.data.data;
              if (basicInfoData && Array.isArray(basicInfoData)) {
                const userIds = basicInfoData.map((user: any) => user.user_id);
                setBasicInfoUsers(userIds);
                console.log('利用者基本情報に登録されているユーザーID:', userIds);
              }
            }
          } catch (error) {
            console.error('利用者基本情報の取得に失敗しました:', error);
          }
        })();
      }
    }
  }, [
    dispatch,
    date,
    cachedAppUsers,
    toggleLifeCheck,
    toggleInterestCheck,
    toggleAdl,
    toggleFunctionalTrainingPlan,
    toggleScientificNursing,
    toggleUserInfo,
  ]);

  // 出力項目が選択されたときに、選択されているタブに応じてデータ取得を行う（非同期で、ローディング表示なし）
  useEffect(() => {
    if (
      location.pathname === '/life_csv_output' &&
      !_.isEmpty(cachedUsers) &&
      !_.isEmpty(cachedAppUsers) &&
      outputItemNames.length > 0 &&
      !isDataFetching
    ) {
      const fetchData = async () => {
        try {
          setIsDataFetching(true);

          // 月の開始日と終了日を取得
          const fromDate = moment(date).startOf('month').format('YYYY-MM-DD');
          const toDate = moment(date).endOf('month').format('YYYY-MM-DD');

          // 各ユーザーのデータを非同期で取得
          const newUsersWithData: { [key: string]: boolean } = {};

          // 選択されているタブに応じて適切なAPIを使用
          if (toggleLifeCheck) {
            // 生活機能チェックデータを取得
            const [isError, lifeCheckAPI] = createLifeCheckAPIInstance();
            if (isError || !lifeCheckAPI) {
              console.error('LifeCheckAPIのインスタンス取得に失敗しました');
              return;
            }

            await Promise.all(
              cachedAppUsers.map(async (uniqueId) => {
                try {
                  const response = await lifeCheckAPI.getLifecheckFunc(uniqueId.unique_id, fromDate, toDate);
                  const data = response.data;

                  if (data && Array.isArray(data) && data.length > 0) {
                    newUsersWithData[uniqueId.app_user_id] = true;
                  }
                } catch (error) {
                  console.error(`Error fetching data for user ${uniqueId.app_user_id}:`, error);
                }
              }),
            );
          } else if (toggleInterestCheck) {
            // 興味関心チェックデータを取得
            const [isError, interestCheckAPI] = createInterestCheckAPIInstance();
            if (isError || !interestCheckAPI) {
              console.error('InterestCheckAPIのインスタンス取得に失敗しました');
              return;
            }

            await Promise.all(
              cachedAppUsers.map(async (uniqueId) => {
                try {
                  // getRecordsByMonthAPIメソッドは複数ユーザーのデータを取得するために設計されているため、
                  // 単一ユーザーのデータを取得するために個別に呼び出す
                  const url = `/interests/users/${uniqueId.unique_id}/records/from/${fromDate}/to/${toDate}`;
                  // TypeScriptエラーを回避するために型アサーションを使用
                  const response = await (interestCheckAPI as any).axiosInstance.get(url);
                  const data = response.data;

                  if (data && !isEmpty(data)) {
                    newUsersWithData[uniqueId.app_user_id] = true;
                  }
                } catch (error) {
                  console.error(`Error fetching data for user ${uniqueId.app_user_id}:`, error);
                }
              }),
            );
          } else if (toggleFunctionalTrainingPlan) {
            // 個別機能訓練計画データを取得
            const [isError, moffAPI] = createMoffAPIInstance();
            if (isError || !moffAPI) {
              console.error('MoffAPIのインスタンス取得に失敗しました');
              return;
            }

            await Promise.all(
              cachedAppUsers.map(async (uniqueId) => {
                try {
                  // MoffAPIを使用して個別機能訓練計画データを取得
                  const response = await moffAPI.getMoffRakuPlansByDateRangeFunc(
                    uniqueId.app_user_id,
                    fromDate,
                    toDate,
                  );
                  const data = response.data;

                  if (data && data.data && Array.isArray(data.data) && data.data.length > 0) {
                    newUsersWithData[uniqueId.app_user_id] = true;
                  }
                } catch (error) {
                  console.error(`Error fetching data for user ${uniqueId.app_user_id}:`, error);
                }
              }),
            );
          } else if (toggleAdl) {
            // ADL維持等データを取得
            const [isError, barthelIndexAPI] = createBarthelIndexAPIInstance();
            if (isError || !barthelIndexAPI) {
              console.error('BarthelIndexAPIのインスタンス取得に失敗しました');
              return;
            }

            await Promise.all(
              cachedAppUsers.map(async (uniqueId) => {
                try {
                  // BarthelIndexAPIには getBarthelIndexFunc ではなく getBiFunc メソッドがある
                  const response = await barthelIndexAPI.getBiFunc(uniqueId.unique_id, fromDate, toDate);
                  const data = response.data;

                  if (data && Array.isArray(data) && data.length > 0) {
                    newUsersWithData[uniqueId.app_user_id] = true;
                  }
                } catch (error) {
                  console.error(`Error fetching data for user ${uniqueId.app_user_id}:`, error);
                }
              }),
            );
          } else if (toggleScientificNursing) {
            // 科学的介護データを取得
            // 科学的介護データは、上のuseEffectで取得したデータを使用する
            if (scientificNursingCarePromotionGlobalState.result?.usersWithData) {
              Object.assign(newUsersWithData, scientificNursingCarePromotionGlobalState.result.usersWithData);
            }
          } else if (toggleUserInfo) {
            // ユーザー情報データを取得
            // TODO ユーザー情報のデータ取得処理を実装する
            console.log('ユーザー情報データを取得');
          }

          console.log('データを持つユーザー:', newUsersWithData);
          setUsersWithData(newUsersWithData);
        } catch (error) {
          console.error('データ取得エラー:', error);
        } finally {
          setIsDataFetching(false);
        }
      };

      fetchData();
    }
  }, [
    location.pathname,
    cachedUsers,
    cachedAppUsers,
    outputItemNames,
    date,
    toggleLifeCheck,
    toggleInterestCheck,
    toggleFunctionalTrainingPlan,
    toggleAdl,
    toggleScientificNursing,
    toggleUserInfo,
  ]);

  // 科学的介護のデータが取得されたときに、usersWithDataを更新する
  useEffect(() => {
    if (toggleScientificNursing && scientificNursingCarePromotionGlobalState.result?.usersWithData) {
      setUsersWithData(scientificNursingCarePromotionGlobalState.result.usersWithData);
    }
  }, [toggleScientificNursing, scientificNursingCarePromotionGlobalState.result]);

  // 利用者基本情報が取得されたときに、basicInfoUsersを更新する（削除：直接APIから取得するように変更）
  // useEffect(() => {
  //   if (toggleUserInfo && moffAPIGlobalState.result && Array.isArray(moffAPIGlobalState.result)) {
  //     // resultから利用者IDのリストを作成（getMoffRakuUsersのレスポンスはresultに格納される）
  //     const userIds = moffAPIGlobalState.result.map((user: any) => user.user_id);
  //     setBasicInfoUsers(userIds);
  //     console.log('利用者基本情報に登録されているユーザーID:', userIds);
  //   }
  // }, [toggleUserInfo, moffAPIGlobalState.result]);

  // 各タブのローディング状態を監視して、isDataFetchingを更新する
  useEffect(() => {
    if (toggleScientificNursing) {
      setIsDataFetching(scientificNursingCarePromotionGlobalState.Loading);
    }

    // タブ切り替え時にローディング状態をリセット
    if (
      !toggleScientificNursing &&
      !toggleLifeCheck &&
      !toggleInterestCheck &&
      !toggleFunctionalTrainingPlan &&
      !toggleAdl &&
      !toggleUserInfo
    ) {
      setIsDataFetching(false);
    }
  }, [
    toggleScientificNursing,
    scientificNursingCarePromotionGlobalState.Loading,
    toggleLifeCheck,
    toggleInterestCheck,
    toggleFunctionalTrainingPlan,
    toggleAdl,
    toggleUserInfo,
  ]);

  // タブの選択が利用者情報の場合のみ全ての利用者情報を表示する
  useEffect(() => {
    if (!_.isEmpty(moffAPIGlobalState.result)) {
      // 全ての利用者情報をソートして設定
      const allUsers = duplicateRepeatSort(UserListField.USERS_SORT_KEYS, moffAPIGlobalState.result, objectQuickSort);
      setCachedUsers(allUsers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moffAPIGlobalState.result]);

  useEffect(() => {
    if (!csvCreateGlobalState.Loading && csvCreateGlobalState?.result) {
      const result = csvCreateGlobalState?.result ?? {};
      (async () => {
        const messages = await Promise.all(
          Object.keys(result).map(async (key, i) => {
            const typedKey = key as keyof typeof CSV_OUTPUT_ITEMS;
            await waitTimeMs((i + 1) * CSV_FILE_DOWNLOAD_INTERVAL);
            const resultItem = result[typedKey as keyof typeof result];
            if (resultItem && resultItem.downloadPath) {
              csvFileDownload(resultItem.downloadPath, key);
            }
            dispatchIdentifierToGTM(`life_csv_output_${CSV_OUTPUT_ITEMS[typedKey].url}`);
            return resultItem && resultItem.downloadPath
              ? `【${CSV_OUTPUT_ITEMS[typedKey].name}】の${date.format('YYYY年MM月')}分のデータを出力しました。`
              : `【${CSV_OUTPUT_ITEMS[typedKey].name}】のデータが${date.format('YYYY年MM月')}に存在しませんでした。`;
          }),
        );
        alert(messages.join('\n'));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvCreateGlobalState.Loading]);

  // 月選択画面 矢印のアクション
  const minusMonthly = () => {
    // dateオブジェクトのクローンを作成してから操作する
    const currentDate = moment(date);
    // 注意: subtract()はオブジェクト自体を変更するので、新しいオブジェクトを作成する
    const lastMonth = moment(currentDate).subtract(1, 'months');
    console.log('minusMonthly: changing date from', date.format('YYYY-MM'), 'to', lastMonth.format('YYYY-MM'));

    // 状態を更新
    setDate(lastMonth);
    setDateYYYYMM(lastMonth.format('YYYY-MM'));

    dispatchIdentifierToGTM('life_csv_change_month');
  };

  const plusMonthly = () => {
    // dateオブジェクトのクローンを作成してから操作する
    const currentDate = moment(date);
    // 注意: add()はオブジェクト自体を変更するので、新しいオブジェクトを作成する
    const nextMonth = moment(currentDate).add(1, 'months');
    console.log('plusMonthly: changing date from', date.format('YYYY-MM'), 'to', nextMonth.format('YYYY-MM'));

    // 状態を更新
    setDate(nextMonth);
    setDateYYYYMM(nextMonth.format('YYYY-MM'));

    dispatchIdentifierToGTM('life_csv_change_month');
  };

  const runAPICsvOutput = async () => {
    // ローディングされている最中はボタンを押しても、APIが送信されない
    if (moffAPIGlobalState.Loading) return;
    if (outputItemNames.length === 0) {
      alert('出力項目を1つ以上選択してください。');
      return;
    }

    // チェックされたユーザーのリストを取得
    const checkedUsers = refUsersList.current?.getCheckedUsers() || [];
    if (checkedUsers.length === 0) {
      alert('出力対象の利用者を1人以上選択してください。');
      return;
    }

    // チェックされたユーザーのIDのリストを作成
    const userIds = checkedUsers.map((user: UserListField.User) => user.user_id);

    const requestParams = {
      year: date.year(),
      month: date.month() + 1,
      service_code: serviceCode.id,
      service_code_support: serviceCodeSupport.id,
      adl_code: adlCode.id,
      userIds: userIds, // チェックされたユーザーのIDのリストを追加
    };
    console.log('requestParams:', requestParams);
    const outputItems = _.filter(CSV_OUTPUT_ITEMS, (item) => outputItemNames.includes(item.name));
    dispatch(csvCreateActions.createCSV(requestParams, outputItems));
  };

  const handleCheckBoxClick = (event: React.ChangeEvent<{ value: unknown }>) => {
    const isPC = osJudgement() === 'PC';
    if (isPC || (!isPC && (event?.target?.value as CSV_OUTPUT_ITEMS['name'][]).length <= 1)) {
      setOutputItemNames(event?.target?.value as CSV_OUTPUT_ITEMS['name'][]);
    }
  };

  const selectBoxChanged = (event: React.ChangeEvent<{ value: unknown }>) => {
    const service_id = event?.target?.value as SERVICE_CODE['id'];
    const service_code_info = _.find(SERVICE_CODE, (code) => code.id === service_id);
    setServiceCode(service_code_info ?? SERVICE_CODE.dayCare);
  };

  const selectCodeSupportBoxChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const service_id = event?.target?.value as SERVICE_CODE_SUPPORT['id'];
    const service_code_info = _.find(SERVICE_CODE_SUPPORT, (code) => code.id === service_id);
    setServiceCodeSupport(service_code_info ?? SERVICE_CODE_SUPPORT.A6);
  };

  const selectADLBoxChanged = (event: React.ChangeEvent<{ value: unknown }>) => {
    const adl_id = event?.target?.value as ADL_CODE['id'];
    const adl_code_info = _.find(ADL_CODE, (code) => code.id === adl_id);
    setAdlCode(adl_code_info ?? ADL_CODE.first);
  };

  // 年月が直接選択されたときの処理
  const handleDateChange = (newDate: moment.Moment) => {
    console.log('handleDateChange: changing date from', date.format('YYYY-MM'), 'to', newDate.format('YYYY-MM'));

    // 状態を更新
    setDate(newDate);
    setDateYYYYMM(newDate.format('YYYY-MM'));

    dispatchIdentifierToGTM('life_csv_change_month');
  };

  const switchToggleLifeCheck = () => {
    setToggleInterestCheck(false);
    setToggleLifeCheck(!toggleLifeCheck);
    setToggleFunctionalTrainingPlan(false);
    setToggleAdl(false);
    setToggleScientificNursing(false);
    setToggleUserInfo(false);
    // 出力項目を生活機能に設定
    setOutputItemNames([CSV_OUTPUT_ITEMS.lifeCheck.name]);
    const gtmKey = CSV_OUTPUT_ITEMS.lifeCheck.url;
    dispatchIdentifierToGTM(`life_csv_display_toggle_${gtmKey}`);
  };

  const switchToggleInterestCheck = () => {
    setToggleInterestCheck(!toggleInterestCheck);
    setToggleLifeCheck(false);
    setToggleFunctionalTrainingPlan(false);
    setToggleAdl(false);
    setToggleScientificNursing(false);
    setToggleUserInfo(false);
    // 出力項目を興味関心に設定
    setOutputItemNames([CSV_OUTPUT_ITEMS.interestCheck.name]);
    const gtmKey = CSV_OUTPUT_ITEMS.interestCheck.url;
    dispatchIdentifierToGTM(`life_csv_display_toggle_${gtmKey}`);
  };

  const switchToggleFunctionalTrainingPlan = () => {
    setToggleInterestCheck(false);
    setToggleLifeCheck(false);
    setToggleFunctionalTrainingPlan(!toggleFunctionalTrainingPlan);
    setToggleAdl(false);
    setToggleScientificNursing(false);
    setToggleUserInfo(false);
    // 出力項目を個別機能訓練計画に設定
    setOutputItemNames([CSV_OUTPUT_ITEMS.functionalTrainingPlan.name]);
    const gtmKey = CSV_OUTPUT_ITEMS.functionalTrainingPlan.url;
    dispatchIdentifierToGTM(`life_csv_display_toggle_${gtmKey}`);
  };

  const switchToggleAdl = () => {
    setToggleInterestCheck(false);
    setToggleLifeCheck(false);
    setToggleFunctionalTrainingPlan(false);
    setToggleAdl(!toggleAdl);
    setToggleScientificNursing(false);
    setToggleUserInfo(false);
    // 出力項目をADL維持等(BI)に設定
    setOutputItemNames([CSV_OUTPUT_ITEMS.barthelIndex.name]);
    const gtmKey = CSV_OUTPUT_ITEMS.barthelIndex.url;
    dispatchIdentifierToGTM(`life_csv_display_toggle_${gtmKey}`);
  };

  const switchToggleScientificNursing = () => {
    setToggleInterestCheck(false);
    setToggleLifeCheck(false);
    setToggleFunctionalTrainingPlan(false);
    setToggleAdl(false);
    setToggleScientificNursing(!toggleScientificNursing);
    setToggleUserInfo(false);
    // 出力項目を科学的介護に設定
    setOutputItemNames([CSV_OUTPUT_ITEMS.scientificNursing.name]);
    const gtmKey = CSV_OUTPUT_ITEMS.scientificNursing.url;
    dispatchIdentifierToGTM(`life_csv_display_toggle_${gtmKey}`);
  };
  const switchToggleUserInfo = () => {
    setToggleInterestCheck(false);
    setToggleLifeCheck(false);
    setToggleFunctionalTrainingPlan(false);
    setToggleAdl(false);
    setToggleScientificNursing(false);
    setToggleUserInfo(!toggleUserInfo);
    // 出力項目を利用者情報に設定
    setOutputItemNames([CSV_OUTPUT_ITEMS.userInfo.name]);
    const gtmKey = CSV_OUTPUT_ITEMS.userInfo.url;
    dispatchIdentifierToGTM(`life_csv_display_toggle_${gtmKey}`);
  };

  /**
   * CSSの描画.
   */
  const RenderCSS: FC = () => {
    return (
      <style type="text/css">
        {/* 縦印刷, 日付・title名ヘッダの余白 */}
        {'@media print{@page {size: portrait; margin-top: 43px;}}'}
        {'@media print{#wrapper { padding:0; margin: 0; } header { display: none; }}'}
        {'@media print{.contentIn { padding-top:0!important; padding-bottom:0!important; }}'}
        {'@media print{.report { padding-bottom:0!important; }}'}
        {/* Edge対策 */}
        {'@media print{tr { page-break-inside: avoid; }}'}
      </style>
    );
  };

  return (
    <>
      <RenderCSS />
      {!moffAPIGlobalState.Loaded ||
      // lifeCheckGlobalState.Loading ||
      // interestCheckGlobalState.Loading ||
      // functionalTrainingPlanGlobalState.Loading ||
      // barthelIndexGlobalState.Loading ||
      csvCreateGlobalState.Loading ? (
        <PageLoader />
      ) : null}
      <LifeCSVOutput
        location={location}
        refUsersList={refUsersList}
        date={date}
        cachedUsers={cachedUsers}
        loaded={moffAPIGlobalState.Loaded}
        serviceCode={serviceCode}
        serviceCodeSupport={serviceCodeSupport}
        adlCode={adlCode}
        outputItemNames={outputItemNames}
        toggleLifeCheck={toggleLifeCheck}
        toggleInterestCheck={toggleInterestCheck}
        toggleFunctionalTrainingPlan={toggleFunctionalTrainingPlan}
        toggleAdl={toggleAdl}
        toggleScientificNursing={toggleScientificNursing}
        toggleUserInfo={toggleUserInfo}
        minusMonthly={minusMonthly}
        plusMonthly={plusMonthly}
        allUsersPrintButtonClickHandler={runAPICsvOutput}
        switchToggleLifeCheck={switchToggleLifeCheck}
        switchToggleInterestCheck={switchToggleInterestCheck}
        switchToggleFunctionalTrainingPlan={switchToggleFunctionalTrainingPlan}
        switchToggleAdl={switchToggleAdl}
        switchToggleScientificNursing={switchToggleScientificNursing}
        switchToggleUserInfo={switchToggleUserInfo}
        handleCheckBoxClick={handleCheckBoxClick}
        selectBoxChanged={selectBoxChanged}
        selectCodeSupportBoxChanged={selectCodeSupportBoxChange}
        selectADLBoxChanged={selectADLBoxChanged}
        onDateChange={handleDateChange}
        onCheckChange={handleCheckChange}
        usersWithData={usersWithData}
        isDataFetching={isDataFetching}
        basicInfoUsers={basicInfoUsers}
      />
    </>
  );
};

export default LifeCSVOutputContainer;
